/**
 * Open-redirect guard (CWE-601) for the login gateway.
 *
 * The login page redirects the user to a `returnUrl` after authentication and
 * passes auth tokens along in that URL. An unvalidated `returnUrl` therefore
 * lets an attacker phish credentials AND exfiltrate tokens to an arbitrary
 * site. This resolves a candidate redirect target to a safe absolute URL, or
 * returns the trusted `fallback` when the candidate is missing, unparseable,
 * not https, or points off the guideline.ai domain.
 *
 * NOTE: the legacy (IE/Excel) login path cannot import ES modules — it uses a
 * byte-for-byte equivalent global function in src/legacy/helper.js. Keep the
 * two in sync.
 */
const ALLOWED_HOST_SUFFIX = 'guideline.ai';

function isAllowedRedirectHost(hostname) {
  const host = String(hostname).toLowerCase();
  // Exact match OR a subdomain. The leading dot is essential: a bare
  // endsWith('guideline.ai') would also accept "guideline.ai.evil.com".
  return host === ALLOWED_HOST_SUFFIX || host.endsWith('.' + ALLOWED_HOST_SUFFIX);
}

function resolveSafeRedirect(
  candidate,
  fallback,
  base = typeof window !== 'undefined' && window.location ? window.location.origin : undefined
) {
  if (!candidate) return fallback;

  let url;
  try {
    // base lets relative paths resolve against the login origin; absolute
    // candidates ignore it. Throws (and we fall back) on garbage input.
    url = new URL(candidate, base);
  } catch (_) {
    return fallback;
  }

  if (url.protocol !== 'https:') return fallback;
  if (!isAllowedRedirectHost(url.hostname)) return fallback;

  return url.href;
}

export default resolveSafeRedirect;
export { isAllowedRedirectHost };
